import React from 'react';
import { Box, Container, Typography, IconButton, Avatar } from '@mui/material';
import { motion } from 'framer-motion';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards, Autoplay, Navigation } from 'swiper/modules';
import { Tilt } from 'react-tilt';
import 'swiper/css';
import 'swiper/css/effect-cards';
import './TestimonialSlider.css';
import '../styles/SectionTitles.css';

const testimonials = [
  {
    id: 1,
    quote: "Virves has revolutionized our digital presence with their innovative solutions. Their team's expertise in creating responsive and user-friendly interfaces has significantly improved our customer engagement.",
    author: "Sarah Johnson",
    role: "CEO, TechVision Inc.",
    focus: "Web Development & UI/UX Design",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
    rating: 5
  },
  {
    id: 2,
    quote: "The mobile app developed by Virves exceeded our expectations. Their attention to detail and commitment to delivering high-performance solutions has helped us reach a wider audience.",
    author: "Michael Chen",
    role: "Product Manager, MobileFirst",
    focus: "Mobile App Development",
    avatar: "https://randomuser.me/api/portraits/men/2.jpg",
    rating: 5
  },
  {
    id: 3,
    quote: "Working with Virves on our e-commerce platform was a game-changer. Their innovative approach to solving complex challenges and ensuring seamless user experience has boosted our online sales significantly.",
    author: "Emily Rodriguez",
    role: "Digital Director, ShopSmart",
    focus: "E-commerce Solutions",
    avatar: "https://randomuser.me/api/portraits/women/3.jpg",
    rating: 5
  },
  {
    id: 4,
    quote: "Virves' expertise in cloud solutions has transformed our business operations. Their scalable and secure infrastructure implementation has given us the confidence to grow our digital services.",
    author: "David Kim",
    role: "CTO, CloudScale Solutions",
    focus: "Cloud Infrastructure",
    avatar: "https://randomuser.me/api/portraits/men/4.jpg",
    rating: 5
  }
];

const TestimonialSlider = () => {
  const defaultTiltOptions = {
    reverse: false,
    max: 15,
    perspective: 1000,
    scale: 1,
    speed: 1000,
    transition: true,
    axis: null,
    reset: true,
    easing: "cubic-bezier(.03,.98,.52,.99)",
  };

  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={sectionVariants}
    >
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, rgba(63, 114, 175, 0.05) 0%, rgba(17, 45, 78, 0.05) 100%)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center'
        }}
        className="testimonial-section"
      >
        {/* CSS-based Particles */}
        {[...Array(20)].map((_, index) => (
          <div
            key={index}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${Math.random() * 10 + 10}s`
            }}
          />
        ))}

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ mb: 8 }}>
            <motion.div variants={titleVariants}>
              <Box className="section-title-row">
                <Typography variant="h2" className="section-title-gradient">
                  Client Success Stories
                </Typography>
                <span className="section-title-line" />
              </Box>
              <Typography variant="h6" className="section-subtitle" >
                Discover how we've helped businesses transform their digital presence
              </Typography>
            </motion.div>
          </Box>

          <motion.div
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              }
            }}
          >
            <Box 
              sx={{ 
                position: 'relative',
                maxWidth: '1000px',
                mx: 'auto',
                '& .swiper-container': {
                  overflow: 'visible'
                }
              }}
            >
              <Swiper
                effect={'cards'}
                grabCursor={true}
                modules={[EffectCards, Autoplay, Navigation]}
                className="testimonial-swiper"
                autoplay={{
                  delay: 5000,
                  disableOnInteraction: false,
                }}
                navigation={{
                  prevEl: '.swiper-button-prev',
                  nextEl: '.swiper-button-next',
                }}
                centeredSlides={true}
              >
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={testimonial.id}>
                    <Tilt options={defaultTiltOptions}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="testimonial-card"
                      >
                        <Box
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '24px',
                            p: { xs: 3, md: 4 },
                            boxShadow: '0 20px 40px rgba(63, 114, 175, 0.1)',
                            border: '1px solid rgba(63, 114, 175, 0.1)',
                            backdropFilter: 'blur(10px)',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <Box className="quote-decoration">
                            <FormatQuoteIcon sx={{ fontSize: '4rem', color: '#3F72AF', opacity: 0.1 }} />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            {/* <Avatar
                              src={testimonial.avatar}
                              sx={{
                                width: 80,
                                height: 80,
                                border: '4px solid #3F72AF',
                                boxShadow: '0 4px 12px rgba(63, 114, 175, 0.2)'
                              }}
                            /> */}
                            <Box sx={{ ml: 2 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: '#112D4E',
                                  fontWeight: 700,
                                  fontSize: { xs: '1.2rem', md: '1.4rem' }
                                }}
                              >
                                {testimonial.author}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#3F72AF',
                                  fontSize: { xs: '0.9rem', md: '1rem' }
                                }}
                              >
                                {testimonial.role}
                              </Typography>
                            </Box>
                          </Box>

                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: { xs: '1.1rem', md: '1.25rem' },
                              color: '#112D4E',
                              mb: 3,
                              fontStyle: 'italic',
                              lineHeight: 1.8,
                              position: 'relative',
                              zIndex: 1
                            }}
                          >
                            "{testimonial.quote}"
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mt: 3
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <motion.span
                                  key={i}
                                  initial={{ opacity: 0, scale: 0 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: i * 0.1 }}
                                >
                                  ⭐
                                </motion.span>
                              ))}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#3F72AF',
                                fontSize: { xs: '0.7rem', md: '1rem' },
                                fontWeight: 600,
                                bgcolor: 'rgba(63, 114, 175, 0.1)',
                                px: 2,
                                py: 1,
                                borderRadius: '20px',
                                backdropFilter: 'blur(5px)'
                              }}
                            >
                              {testimonial.focus}
                            </Typography>
                          </Box>
                        </Box>
                      </motion.div>
                    </Tilt>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* <div className="swiper-button-prev custom-swiper-button">
                <IconButton>
                  <NavigateBeforeIcon />
                </IconButton>
              </div>
              <div className="swiper-button-next custom-swiper-button">
                <IconButton>
                  <NavigateNextIcon />
                </IconButton>
              </div> */}
            </Box>
          </motion.div>
        </Container>
      </Box>
    </motion.div>
  );
};

export default TestimonialSlider; 